import type { PropsWithChildren } from "react";
import { lazy, Suspense, useEffect } from "react";
import {
  type HtmlLinkDescriptor,
  Links,
  type LinksFunction,
  type LoaderFunctionArgs,
  Meta,
  Outlet,
  Scripts,
  useLoaderData,
} from "react-router";

import {
  Language,
  SporProvider,
  theme as sporTheme,
  theme,
} from "@vygruppen/spor-react";
import type { StyleFunctionProps } from "@chakra-ui/react";
import {
  cookieStorageManager,
  cookieStorageManagerSSR,
  mergeThemeOverride,
} from "@chakra-ui/react";

import { OpfsWorkerProvider } from "~/features/cache/opfs-context";
import { getDayPlanOverride } from "~/features/day-plan/cookie.server";
import { AbilityProvider } from "~/features/permissions/AbilityContext";
import { PushNotificationsModal } from "~/features/push-notifications/PushNotificationsModal";
import { PushSubscriptionProvider } from "~/features/push-notifications/PushSubscriptionContext";
import { ServiceWorkerUpdaterProvider } from "~/features/pwa/service-worker-updater";
import { useNavigationSync } from "~/features/pwa/use-navigation-sync";
import { PrefetchQueries } from "~/features/query-cache/PrefetchQueries";
import { QueryCacheProvider } from "~/features/query-cache/provider";
import { syncEmployeeId } from "~/features/storage/sync";
import { CurrentTimeProvider } from "~/features/time/context";
import globalStyles from "~/global.css?url";
import { APP_TITLE } from "~/utils/constants";
import { RUN_WITH_MOCKS } from "~/utils/environment.server";
import { getResponsiveValue } from "~/utils/responsive";
import { withEnv } from "~/utils/string";
import { type Route } from "../.react-router/types/app/+types/root";
import { withResolvedRoles } from "./features/auth/utils";
import { useEnrichDatadogRUMGlobalContext } from "./features/logging/use-enrich-datadog-rum-global-context";
import { getAnonymousUserId } from "./features/logging/utils";
import { canUseFeature } from "./features/permissions/ability.server";
import { QueryUpdateContextProvider } from "./features/query-update/QueryUpdateContext";
import { useClientStyleReset } from "./features/style/client-context";
import { useIsTrainInsights } from "./features/train-insights/use-is-train-insights";
import { ClientOnly } from "./utils/client-only";
import { resolveEmployeeNumber } from "./utils/user";

import { datadogRum } from "@datadog/browser-rum";
import { withEmotionCache } from "@emotion/react";
import reactPdfCoreStyles from "@react-pdf-viewer/core/lib/styles/index.css?url";
import reactPdfDefaultStyles from "@react-pdf-viewer/default-layout/lib/styles/index.css?url";
import pdfSearchStyles from "@react-pdf-viewer/search/lib/styles/index.css?url";
import mapLibreStyles from "maplibre-gl/dist/maplibre-gl.css?url";
import { $path } from "safe-routes";
import { type Jsonify } from "type-fest";

export { RootErrorBoundary as ErrorBoundary } from "~/features/error-handling/RootErrorBoundary";

export const meta = ({ loaderData }: Route.MetaArgs) => {
  const appTitles = loaderData?.isTrainInsights
    ? APP_TITLE.trainInsights
    : APP_TITLE.topas;

  return [
    {
      title: withEnv(appTitles.short, loaderData?.env),
    },
    {
      charSet: "utf-8",
    },
    {
      name: "viewport",
      content: "width=device-width,initial-scale=1",
    },
    {
      name: "description",
      content: appTitles.long,
    },
    {
      name: "theme-color",
      content: theme.colors.teal["400"],
    },
    {
      name: "robots",
      content: "noindex, nofollow",
    },
    {
      httpEquiv: "Accept-CH",
      content: "Sec-CH-UA-Model, Sec-CH-UA-Platform-Version",
    },
  ];
};

export const links: LinksFunction = () => [
  {
    rel: "manifest",
    href: $path("/resources/manifest.webmanifest"),
  },
  ...[
    "VyDisplay-Medium",
    "VySans-Regular",
    "VySans-Light",
    "VySans-Bold",
  ].map<HtmlLinkDescriptor>((font) => ({
    rel: "preload",
    href: `https://www.vy.no/styles/font/${font}.woff2`,
    as: "font",
    crossOrigin: "anonymous",
    type: "font/woff2",
  })),
  {
    rel: "icon",
    href: "/favicon.svg",
    type: "image/svg+xml",
  },
  {
    rel: "stylesheet",
    href: globalStyles,
  },
  {
    rel: "stylesheet",
    href: pdfSearchStyles,
  },
  {
    rel: "stylesheet",
    href: mapLibreStyles,
  },
  {
    rel: "apple-touch-icon",
    href: "/icons/apple-icon-180x180.png",
    type: "image/png",
  },
  {
    rel: "apple-touch-icon-precomposed",
    href: "/icons/apple-icon-180x180.png",
    type: "image/png",
  },
  {
    rel: "stylesheet",
    href: reactPdfCoreStyles,
  },
  {
    rel: "stylesheet",
    href: reactPdfDefaultStyles,
  },
];

export async function loader({ request, context }: LoaderFunctionArgs) {
  const dayPlanOverride = await getDayPlanOverride(request, context);

  // TODO: remove this override once roleFromEntraGroup is enabled for everyone
  // Wanted to do this in cognito-server instead, but can't access context easily there.
  // This function bases roles isConductor/isDriver on new Entra AD groups if flag is turned on,
  // otherwise fall back to old user.position-based function.
  const canUseRoleFromEntraGroup = canUseFeature(context, "roleFromEntraGroup");
  const user =
    context.session?.user &&
    withResolvedRoles(context.session.user, canUseRoleFromEntraGroup);

  return {
    breakpoint: getResponsiveValue(request, {
      mobile: "base",
      tablet: "sm",
      desktop: "md",
    }),
    user,
    dayPlanOverride,
    featureToggleOverride: context.featureToggleOverride,
    vapidPublicKey: process.env.VAPID_PUBLIC_KEY,
    appConfig: context.appConfig,
    env: process.env.VY_ENV,
    version: process.env.VERSION,
    datadogConfig: {
      applicationId: process.env.DATADOG_APPLICATION_ID,
      clientToken: process.env.DATADOG_CLIENT_TOKEN,
      service: process.env.DATADOG_SERVICE,
    },
    devToolsConfig: !!user?.rbac?.isDeveloper && {
      hasTanstackQueryDevTools: true,
      hasFeatureToggleDevTools: true,
      hasDayPlanOverride: true,
    },
    cookies: request.headers.get("Cookie") ?? "",
    isMocking: RUN_WITH_MOCKS,
    slackFeedbackChannelId: process.env.FEEDBACK_SLACK_CHANNEL_ID,
    isTrainInsights: process.env.TRAIN_INSIGHTS === "true",
  };
}

export type RootLoaderData = Jsonify<Awaited<ReturnType<typeof loader>>>;

const Document = withEmotionCache(
  (
    {
      children,
      isAuthenticated,
      cookies,
      env,
      datadogConfig,
    }: PropsWithChildren<{
      cookies?: string;
      isAuthenticated: boolean;
      env?: string;
      datadogConfig?: RootLoaderData["datadogConfig"];
    }>,
    emotionCache,
  ) => {
    useClientStyleReset(emotionCache);
    const theme = mergeThemeOverride(sporTheme, {
      styles: {
        global: (props: StyleFunctionProps) => ({
          body: {
            bg: props.colorMode === "dark" ? "darkGrey" : "white",
            color: props.colorMode === "dark" ? "mint" : "darkGrey",
          },
        }),
      },
    });

    let colorModeManager: typeof cookieStorageManager | undefined;
    if (typeof document !== "undefined") {
      // Use client-side cookies if we can, since they will be more up-to-date, given our caching strategy.
      colorModeManager = cookieStorageManager;
    } else if (cookies) {
      colorModeManager = cookieStorageManagerSSR(cookies);
    }

    return (
      // "no" as in norwegian and "no" as in "don't translate this page"
      <html lang="no" translate="no">
        <head>
          <Meta />
          <Links />
          {env && (
            <script
              dangerouslySetInnerHTML={{
                __html: `window.__vyEnv=${JSON.stringify(env)};`,
              }}
            />
          )}
          {datadogConfig && (
            <script
              dangerouslySetInnerHTML={{
                __html: `window.__datadogConfig=${JSON.stringify(datadogConfig)};`,
              }}
            />
          )}
        </head>
        <body>
          <SporProvider
            theme={theme}
            language={Language.NorwegianBokmal}
            colorModeManager={colorModeManager}
          >
            <QueryCacheProvider>
              <AbilityProvider>
                <PushSubscriptionProvider>
                  <ServiceWorkerUpdaterProvider
                    isAuthenticated={isAuthenticated}
                  >
                    <QueryUpdateContextProvider>
                      <CurrentTimeProvider>{children}</CurrentTimeProvider>
                    </QueryUpdateContextProvider>
                  </ServiceWorkerUpdaterProvider>
                </PushSubscriptionProvider>
              </AbilityProvider>
            </QueryCacheProvider>
          </SporProvider>
          <Scripts />
        </body>
      </html>
    );
  },
);

const DevToolsProvider = lazy(() =>
  import("~/features/dev-tools/DevToolsProvider").then((m) => ({
    default: m.DevToolsProvider,
  })),
);

export default function App() {
  const {
    user,
    dayPlanOverride,
    cookies,
    devToolsConfig,
    isMocking,
    env,
    datadogConfig,
  } = useLoaderData<RootLoaderData>();

  const { isTrainInsights } = useIsTrainInsights();

  useNavigationSync(!!user);
  useEnrichDatadogRUMGlobalContext();

  useEffect(() => {
    void syncEmployeeId(
      resolveEmployeeNumber(user?.employeeId, dayPlanOverride),
    );
  }, [dayPlanOverride, user?.employeeId]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const role = user.rbac.isConductor
      ? "conductor"
      : user.rbac.isDriver
        ? "driver"
        : "other";

    const id = user.rbac.isDeveloper ? user.employeeId : getAnonymousUserId();

    datadogRum.setUser({
      id,
      role,
    });

    if (user.rbac.isDeveloper) {
      // Start session replay manually for developers to help with showing what we are missing by keeping it off for everyone else.
      datadogRum.startSessionReplayRecording({ force: true });
    }
  }, [user]);

  if (isMocking) {
    console.log("Skip prefetching queries in mock mode (for now)");
  }

  return (
    <Document
      cookies={cookies}
      isAuthenticated={!!user}
      env={env}
      datadogConfig={datadogConfig}
    >
      <OpfsWorkerProvider>
        {!!user && !isMocking && !isTrainInsights && <PrefetchQueries />}
        {user?.employeeId && (
          <PushNotificationsModal employeeId={user.employeeId} />
        )}
        <Outlet />
        {devToolsConfig && (
          <ClientOnly>
            {() => (
              <Suspense fallback={null}>
                <DevToolsProvider devToolsConfig={devToolsConfig} />
              </Suspense>
            )}
          </ClientOnly>
        )}
      </OpfsWorkerProvider>
    </Document>
  );
}
